import { useQuery, useMutation, UseMutationOptions } from "@tanstack/react-query";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";

export const getAllContacts = async (list_id: string | number) => {
  const endpoint = `/cdk-collections/v2/lists/${list_id}/export`;
  const res = await axios.get(endpoint);
  const formattedRes = res.data.map((v: Contact) => ({
    ...v
  }));

  return formattedRes;
};

const getContactsByList = async ({
  list_id,
  offset = 0,
  hh_id,
  agent_id,
}: {
  list_id: string | number;
  offset?: number;
  hh_id?: string | undefined;
  agent_id: number | undefined;
}): Promise<{
  data: Contact[];
  has_next: boolean;
  total: number;
}> => {
  let endpoint = `https://api.momentumsolar.io/cdk-collections/v2/leads?mts_collections_list_id=${list_id}&offset=${offset}&end_utc=null`;
  if(hh_id) endpoint += `&household_id=${hh_id.toUpperCase()}`;
  if(agent_id) endpoint += `&mts_collections_agents_id=${agent_id}`;
  const res = await axios.get(endpoint);
  const formattedData = res?.data?.data?.map((v: Contact) => ({
    ...v
  }));

  return {
    data: formattedData,
    has_next: res?.data?.has_next,
    total: res?.data?.total
  };
};

export const useContacts = ({
  list_id,
  page,
  limit,
  hh_id,
  agent_id,
}: {
  list_id: string | number;
  page: number;
  limit: number;
  hh_id?: string;
  agent_id: number | undefined;
}) => {
  const query = useQuery({
    queryKey: ["contacts-by-list", list_id, page, limit, hh_id || "", agent_id ?? ""],
    queryFn: () =>
      getContactsByList({ list_id, offset: page * limit, hh_id, agent_id }),
    placeholderData: (previousData) => previousData,
  });

  const contacts = query?.data?.data ?? [];
  const total = query?.data?.total ?? 0;

  const hasNextPage = (page + 1) * limit < total;
  const hasPreviousPage = page > 0;

  return {
    ...query,
    contacts,
    total,
    hasNextPage,
    hasPreviousPage,
  };
};

export const useAllContacts = ({ list_id }: { list_id: string | number }) => {
  return useQuery({
    queryKey: ["all-contacts", list_id],
    queryFn: () => getAllContacts(list_id),
    enabled: !!list_id,
  });
};

export const deleteContact = async ({contact_id}: DeleteContactPostBody): Promise<void> => {
  const endpoint = `/cdk-collections/v2/leads/${contact_id}`;
  const res = await axios.delete(endpoint);
  return res.data;
};

export const useDeleteContactMutation = (
  options?: UseMutationOptions<void, Error, DeleteContactPostBody>,
) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, DeleteContactPostBody>({
    mutationFn: deleteContact,
    ...options,
    onSuccess: (...args) => {
      // Internal behavior
      queryClient.invalidateQueries({
        queryKey: ["contacts-by-list"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["all-contacts"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["agents-by-list"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["contact-lists"],
        exact: false,
      });

      // Call the user-passed onSuccess if it exists
      options?.onSuccess?.(...args);
    },
  });
};

const batchUploadContacts = async (
  body: BatchUploadContactsBody,
): Promise<void> => {
  const endpoint = "/cdk-collections/v2/leads/batch";

  const res = await axios.post(endpoint, body);
  return res.data;
};

export const useBatchUploadContactsMutation = (
  options?: UseMutationOptions<void, Error, BatchUploadContactsBody>,
) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, BatchUploadContactsBody>({
    mutationFn: batchUploadContacts,
    ...options,
    onSuccess: (...args) => {
      // Internal behavior
      queryClient.invalidateQueries({
        queryKey: ["contacts-by-list"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["all-contacts"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["agents-by-list"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["contact-lists"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["contact-lists-archived"],
        exact: false,
      });

      // Call the user-passed onSuccess if it exists
      options?.onSuccess?.(...args);
    },
  });
};

export const postSingleContact = async (
  body: PostSingleContactBody,
): Promise<void> => {
  const endpoint = "/cdk-collections/v2/leads/";
  const res = await axios.post(endpoint, body);
  return res.data;
};

export const useCreateSingleContactMutation = (
  options?: UseMutationOptions<void, Error, PostSingleContactBody>,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, PostSingleContactBody>({
    mutationFn: postSingleContact,
    ...options,
    onSuccess: (...args) => {
      // Internal behavior
      queryClient.invalidateQueries({
        queryKey: ["contacts-by-list"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["all-contacts"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["agents-by-list"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["contact-lists"],
        exact: false,
      });

      // Call the user-passed onSuccess if it exists
      options?.onSuccess?.(...args);
    },
  });
};